import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Target, Users, Zap, MapPin, ChevronRight } from 'lucide-react';
import SEO, { PAGE_SEO } from './SEO';

const About: React.FC = () => {
  return (
    <>
      <SEO {...PAGE_SEO.about} url="/about" />
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-600 via-green-700 to-emerald-800">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-300 rounded-full blur-3xl"></div>
          </div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-32">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4 text-yellow-300" />
              <span className="text-white/90 text-sm font-medium">Built in Georgia</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Two Brothers.<br />
              <span className="text-yellow-300">One Passion.</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              ClubIQ started on the fairways of Georgia, where we learned that smarter golf beats harder golf every time.
            </p>
          </div>
        </div>
        
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
          </svg>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Story Content */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-5 text-gray-600 text-lg leading-relaxed">
                <p>
                  We grew up in Georgia with golf clubs in our hands before we could tie our shoes. Our father taught us the basics on public courses around Atlanta, and our grandfather — a scratch golfer in his day — showed us what dedication to the game really looked like.
                </p>
                <p>
                  Those weekend rounds weren't just about golf. They were about three generations walking 18 holes together, sharing stories between shots, and passing down a love for the game that's now in our blood.
                </p>
                <p>
                  As we got older, we noticed something: the golfers who improved fastest weren't always the ones with the best swings or the newest equipment. They were the ones who <strong className="text-gray-900">understood their game</strong> — who knew exactly how far they hit each club and built their bag around their actual distances, not marketing promises.
                </p>
              </div>
            </div>
            
            {/* Visual Element */}
            <div className="relative">
              <div className="bg-gradient-to-br from-green-100 to-emerald-50 rounded-3xl p-8 md:p-12">
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-green-600 mb-2">3</div>
                    <div className="text-gray-600 text-sm">Generations of golfers</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-green-600 mb-2">25+</div>
                    <div className="text-gray-600 text-sm">Years on the course</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-green-600 mb-2">GA</div>
                    <div className="text-gray-600 text-sm">Built in Georgia</div>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
                    <div className="text-gray-600 text-sm">Passion for golf</div>
                  </div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-100 rounded-full opacity-60 blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-green-100 rounded-full opacity-60 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* The Aha Moment */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Moment Everything Changed
            </h2>
          </div>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gray-100">
            <div className="prose prose-lg max-w-none text-gray-600">
              <p className="text-xl leading-relaxed mb-6">
                We started tracking our clubs differently — not by feel, but by <em>actual data</em>. We measured our real distances, identified the gaps in our bags, and made small adjustments based on math instead of guesswork.
              </p>
              <p className="text-xl leading-relaxed mb-6">
                The results? Our handicaps dropped. Our confidence soared. And club selection went from a source of anxiety to one of our biggest advantages.
              </p>
              <p className="text-xl leading-relaxed font-medium text-gray-900">
                We built ClubIQ because we wanted every golfer — from weekend warriors to serious competitors — to have access to the same insights that transformed our games. No expensive equipment required. No confusing spreadsheets. Just clear, actionable intelligence about your clubs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Believe
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we build at ClubIQ
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Target,
                title: "Data Over Feel",
                description: "Your actual distances matter more than what the club is supposed to do.",
                color: "green"
              },
              {
                icon: Users,
                title: "Golf is for Everyone",
                description: "Whether you're a 30 handicap or scratch, you deserve tools that help you improve.",
                color: "blue"
              },
              {
                icon: Zap,
                title: "Simple Wins",
                description: "Complex analysis means nothing if you can't use it on the course.",
                color: "yellow"
              },
              {
                icon: Heart,
                title: "Love the Game",
                description: "We build for golfers because we are golfers. This isn't just a product to us.",
                color: "red"
              }
            ].map((value, index) => {
              const Icon = value.icon;
              const colorClasses = {
                green: "bg-green-100 text-green-600",
                blue: "bg-blue-100 text-blue-600",
                yellow: "bg-yellow-100 text-yellow-600",
                red: "bg-red-100 text-red-600"
              };
              
              return (
                <div key={index} className="text-center">
                  <div className={`w-16 h-16 ${colorClasses[value.color as keyof typeof colorClasses]} rounded-2xl flex items-center justify-center mx-auto mb-5`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                An Asgard Technologies Project
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                ClubIQ is developed by Asgard Technologies, LLC, a Georgia-based company focused on building tools that make complex things simple. We're golfers first, engineers second, and we think that shows in everything we build.
              </p>
              <div className="flex items-center gap-3 text-gray-400">
                <MapPin className="w-5 h-5 text-green-400" />
                <span>Georgia, USA</span>
              </div>
            </div>
            
            <div className="flex justify-center md:justify-end">
              <div className="bg-gray-800 rounded-2xl p-8 text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <img 
                    src="/Images/New Logo Image.png" 
                    alt="ClubIQ Logo" 
                    className="h-12 w-auto rounded-lg"
                  />
                  <span className="text-2xl font-bold">ClubIQ</span>
                </div>
                <p className="text-gray-400 text-sm">The intelligence behind better golf</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-green-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Play Smarter?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join thousands of golfers who've discovered what knowing their real distances can do for their game.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white text-green-700 px-8 py-4 rounded-xl text-lg font-bold hover:bg-green-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            Get Started Free
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
